import { __awaiter } from "tslib";
import { useEffect, useRef, useState } from 'react';
import { useAuth, useConfig } from '@marvelapp/ballpark-application';
import { fetchApi } from '@marvelapp/core';
export const useInsightsData = (projectPk, dataQuery) => {
    const [insightsData, setInsightsData] = useState(null);
    const [isLoadingInsights, setIsLoadingInsights] = useState(true);
    const [insightsAnalysis, setInsightsAnalysis] = useState(null);
    const [isLoadingInsightsAnalysis, setIsLoadingInsightsAnalysis] = useState(false);
    const [insightsAnalysisError, setInsightsAnalysisError] = useState(null);
    const config = useConfig();
    const accessToken = useAuth();
    const requestInProgressRef = useRef(false);
    const requestIdRef = useRef(0);
    // Fetch regular insights data
    useEffect(() => {
        if (!dataQuery)
            return;
        let cancelled = false;
        const fetchInsightsData = () => __awaiter(void 0, void 0, void 0, function* () {
            setIsLoadingInsights(true);
            try {
                const response = yield fetchApi(`${config.edge.host}/api/getInsightsData`, {
                    method: 'POST',
                    accessToken,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ projectPk: Number(projectPk), dataQuery }),
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch insights data');
                }
                const data = yield response.json();
                if (cancelled)
                    return;
                setInsightsData(data);
            }
            catch (error) {
                console.error('Error fetching insights data:', error);
            }
            finally {
                setIsLoadingInsights(false);
            }
        });
        fetchInsightsData();
        return () => {
            cancelled = true;
        };
    }, [projectPk, config.edge.host, accessToken, dataQuery]);
    // Fetch summary AI insights analysis
    useEffect(() => {
        if (!dataQuery || !insightsData)
            return;
        const fetchAIInsights = () => __awaiter(void 0, void 0, void 0, function* () {
            if (requestInProgressRef.current)
                return;
            requestInProgressRef.current = true;
            const currentRequestId = ++requestIdRef.current;
            setIsLoadingInsightsAnalysis(true);
            setInsightsAnalysisError(null);
            try {
                const response = yield fetchApi(`${config.edge.host}/api/analyzeInsights`, {
                    method: 'POST',
                    accessToken,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        projectPk: Number(projectPk),
                        dataQuery,
                    }),
                });
                if (!response.ok) {
                    throw new Error(`Error fetching AI insights: ${response.statusText}`);
                }
                if (currentRequestId === requestIdRef.current) {
                    const data = yield response.json();
                    setInsightsAnalysis(data);
                }
            }
            catch (err) {
                console.error('Failed to fetch AI insights:', err);
                if (currentRequestId === requestIdRef.current) {
                    setInsightsAnalysisError(err instanceof Error
                        ? err.message
                        : 'Failed to generate AI insights');
                }
            }
            finally {
                if (currentRequestId === requestIdRef.current) {
                    setIsLoadingInsightsAnalysis(false);
                }
                requestInProgressRef.current = false;
            }
        });
        fetchAIInsights();
    }, [projectPk, config.edge.host, accessToken, dataQuery, insightsData]);
    return {
        insightsData,
        isLoadingInsights,
        insightsAnalysis,
        isLoadingInsightsAnalysis,
        insightsAnalysisError,
    };
};
